import { Injectable } from '@angular/core';
import { Capacitor, registerPlugin } from '@capacitor/core';

interface EmbeddedVideoPlayerPlugin {
  create(options: {
    playerId: string;
    url: string;
    x: number;
    y: number;
    width: number;
    height: number;
    autoplay?: boolean;
    loop?: boolean;
    muted?: boolean;
  }): Promise<{ result: boolean }>;
  update(options: { playerId: string; x: number; y: number; width: number; height: number }): Promise<{ result: boolean }>;
  play(options: { playerId: string }): Promise<void>;
  pause(options: { playerId: string }): Promise<void>;
  stop(options: { playerId: string }): Promise<void>;
  setMuted(options: { playerId: string; muted: boolean }): Promise<void>;
  setGlobalMuted(options: { muted: boolean }): Promise<void>;
  destroy(options: { playerId: string }): Promise<void>;
}

const EmbeddedVideoPlayer = registerPlugin<EmbeddedVideoPlayerPlugin>('EmbeddedVideoPlayer');

@Injectable({
  providedIn: 'root',
})
export class EmbeddedVideoPlayerService {
  isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }

  async createPlayer(options: {
    playerId: string;
    url: string;
    rect: DOMRect;
    autoplay?: boolean;
    loop?: boolean;
    muted?: boolean;
  }): Promise<boolean> {
    if (!this.isNativePlatform()) {
      return false;
    }

    try {
      console.log('[EmbeddedVideoPlayer] availability', {
        embedded: Capacitor.isPluginAvailable('EmbeddedVideoPlayer'),
        embeddedPlugin: Capacitor.isPluginAvailable('EmbeddedVideoPlayerPlugin'),
      });
      await EmbeddedVideoPlayer.create({
        playerId: options.playerId,
        url: options.url,
        x: options.rect.x,
        y: options.rect.y,
        width: options.rect.width,
        height: options.rect.height,
        autoplay: options.autoplay ?? true,
        loop: options.loop ?? false,
        muted: options.muted ?? false,
      });
      return true;
    } catch (error) {
      console.warn('[EmbeddedVideoPlayer] create failed', error);
      return false;
    }
  }

  async updateLayout(playerId: string, rect: DOMRect): Promise<void> {
    if (!this.isNativePlatform()) {
      return;
    }

    try {
      await EmbeddedVideoPlayer.update({
        playerId,
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
      });
    } catch (error) {
      console.warn('[EmbeddedVideoPlayer] update failed', error);
    }
  }

  async destroy(playerId: string): Promise<void> {
    if (!this.isNativePlatform()) {
      return;
    }

    try {
      await EmbeddedVideoPlayer.destroy({ playerId });
    } catch (error) {
      console.warn('[EmbeddedVideoPlayer] destroy failed', error);
    }
  }

  async setMuted(playerId: string, muted: boolean): Promise<void> {
    if (!this.isNativePlatform()) {
      return;
    }

    try {
      await EmbeddedVideoPlayer.setMuted({ playerId, muted });
    } catch (error) {
      console.warn('[EmbeddedVideoPlayer] setMuted failed', error);
    }
  }

  async setGlobalMuted(muted: boolean): Promise<void> {
    if (!this.isNativePlatform()) {
      return;
    }

    try {
      await EmbeddedVideoPlayer.setGlobalMuted({ muted });
    } catch (error) {
      console.warn('[EmbeddedVideoPlayer] setGlobalMuted failed', error);
    }
  }
}
